import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import en from './locales/en';
import es from './locales/es';
import ca from './locales/ca';
import it from './locales/it';
import fr from './locales/fr';

export const SUPPORTED_LOCALES = ['en', 'es', 'ca', 'it', 'fr'] as const;
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
      es: { translation: es },
      ca: { translation: ca },
      it: { translation: it },
      fr: { translation: fr },
    },
    fallbackLng: 'en',
    supportedLngs: [...SUPPORTED_LOCALES],
    detection: {
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: 'trip-planner-lang',
      caches: ['localStorage'],
    },
    interpolation: { escapeValue: false },
  });

export default i18n;

/**
 * Base language code, region tag dropped ('es-ES' → 'es'), restricted to what
 * the app ships. Anything sent to the API must go through this: the backend
 * validates trip.contentLanguage against the same five codes and would reject
 * a region-tagged one.
 */
export function currentLocale(): SupportedLocale {
  const base = (i18n.resolvedLanguage ?? i18n.language ?? 'en').toLowerCase().split('-')[0];
  return (SUPPORTED_LOCALES as readonly string[]).includes(base) ? base as SupportedLocale : 'en';
}

/**
 * Locale for transactional emails. The backend only ships es/ca/en templates and
 * its zod schemas reject anything else — sending raw `i18n.language` from an
 * Italian or French session would 400 the request.
 */
export function emailLocale(): 'es' | 'ca' | 'en' {
  const base = currentLocale();
  return base === 'es' || base === 'ca' ? base : 'en';
}

export const LANGUAGES = [
  { code: 'en', label: 'EN', name: 'English' },
  { code: 'es', label: 'ES', name: 'Español' },
  { code: 'ca', label: 'CA', name: 'Català' },
  { code: 'it', label: 'IT', name: 'Italiano' },
  { code: 'fr', label: 'FR', name: 'Français' },
];
